import { useState } from "react";
import { Calendar, Clock, Users, ChevronRight } from "lucide-react";

const DAY_COLORS = {
  Almuerzo: "#F5C518",
  Kayak: "#0072A4",
  "Paddle Surf": "#3CB44B",
  BTT: "#F58220",
  Orientación: "#F59E0B",
  "Aula Naturaleza": "#27AE60",
  "HADO AR": "#8B5CF6",
  "Paddle Gigante": "#06B6D4",
  "HADO + Cierre": "#8B5CF6",
  "Juegos grupo": "#EC4899",
  "Cont. actividad": "#94A3B8",
  "Kayak / Paddle": "#0072A4",
};

function getColor(activity) {
  for (const key of Object.keys(DAY_COLORS)) {
    if (activity && activity.toLowerCase().includes(key.toLowerCase())) return DAY_COLORS[key];
  }
  return "#94A3B8";
}

export default function ScheduleSection({ schedule }) {
  const [activeDay, setActiveDay] = useState(0);
  const semanas = schedule.semanas || [];
  const dias = schedule.dias || [];
  const currentDay = dias[activeDay];
  const sectionLabel = schedule.section_label || "Calendario y Horarios";
  const sectionTitle = schedule.title || "¿Cuándo y cómo?";
  const sectionSubtitle = schedule.subtitle || "El campamento se desarrolla en dos semanas de pura aventura. ¡Apúntate a una o a las dos!";

  return (
    <section id="horarios" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#F58220]/10 rounded-full px-5 py-2 mb-5">
            <Calendar size={16} color="#F58220" />
            <span className="text-[#F58220] font-['Montserrat'] font-bold text-[10px] uppercase tracking-[0.18em]">{sectionLabel}</span>
          </div>
          <h2
            className="font-['Montserrat'] font-extrabold text-[#0F172A] mb-5"
            style={{ fontSize: "clamp(1.25rem, 1.8vw, 1.625rem)", letterSpacing: "-0.01em", lineHeight: 1.2 }}
            data-testid="schedule-title"
          >
            {sectionTitle}
          </h2>
          <p className="text-[#475569] font-['Montserrat'] text-sm md:text-[0.9375rem] max-w-2xl mx-auto leading-relaxed">
            {sectionSubtitle}
          </p>
        </div>

        {/* Week cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {semanas.map((semana) => (
            <div
              key={semana.numero}
              data-testid={`week-card-${semana.numero}`}
              className="rounded-2xl p-6 border-2 border-[#0072A4]/20 bg-gradient-to-br from-[#F0F8FF] to-white hover:border-[#0072A4] hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#0072A4] flex items-center justify-center text-white font-['Montserrat'] font-bold text-lg">
                  {semana.numero}
                </div>
              <h3 className="font-['Montserrat'] font-bold text-base text-[#0072A4]">Semana {semana.numero}</h3>
              </div>
              <div className="flex items-center gap-2 text-[#0F172A] font-['Montserrat'] font-semibold text-lg mb-2">
                <Calendar size={18} color="#F58220" />
                {semana.fecha_inicio} — {semana.fecha_fin} de {semana.year}
              </div>
              <div className="flex items-center gap-2 text-[#475569] font-['Montserrat'] text-sm">
                <Clock size={15} color="#0072A4" />
                Horario: {schedule.horario_normal || "9:00 - 14:00"}
              </div>
              {schedule.horario_madrugadores && (
                <div className="mt-1 flex items-center gap-2 text-[#475569] font-['Montserrat'] text-sm">
                  <Clock size={15} color="#3CB44B" />
                  Madrugadores: {schedule.horario_madrugadores}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Daily schedule */}
        {dias.length > 0 && (
          <div className="bg-[#F0F8FF] rounded-3xl p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Users size={20} color="#0072A4" />
              <h3 className="font-['Montserrat'] font-bold text-base text-[#0F172A]">Cronograma Semanal</h3>
              <span className="text-[#475569] font-['Montserrat'] text-sm ml-2">(Grupos G1 y G2 rotan actividades)</span>
            </div>

            {/* Day tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
              {dias.map((dia, i) => (
                <button
                  key={dia.dia}
                  onClick={() => setActiveDay(i)}
                  data-testid={`day-tab-${dia.dia}`}
                  className={`flex-shrink-0 px-4 py-2 rounded-full font-['Montserrat'] font-semibold text-sm transition-all ${
                    activeDay === i
                      ? "bg-[#0072A4] text-white shadow-md"
                      : "bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#0072A4]"
                  }`}
                >
                  {dia.dia}
                </button>
              ))}
            </div>

            {/* Day schedule */}
            {currentDay && (
              <div className="space-y-3">
                {/* Column headers */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center text-xs font-['Montserrat'] font-bold text-[#475569] uppercase tracking-wide">Hora</div>
                  <div className="text-center text-xs font-['Montserrat'] font-bold text-[#0072A4] uppercase tracking-wide">Grupo 1</div>
                  <div className="text-center text-xs font-['Montserrat'] font-bold text-[#3CB44B] uppercase tracking-wide">Grupo 2</div>
                </div>
                {currentDay.actividades.map((slot, i) => (
                  <div
                    key={`${slot.hora || "slot"}-${i}`}
                    className="grid grid-cols-3 gap-3 items-center bg-white rounded-xl p-3 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Clock size={14} color="#94A3B8" />
                      <span className="font-['Montserrat'] text-xs font-semibold text-[#475569]">{slot.hora}</span>
                    </div>
                    <div
                      className="text-center rounded-lg px-3 py-2 font-['Montserrat'] font-bold text-xs text-white"
                      style={{ background: getColor(slot.g1) }}
                    >
                      {slot.g1}
                    </div>
                    <div
                      className="text-center rounded-lg px-3 py-2 font-['Montserrat'] font-bold text-xs text-white"
                      style={{ background: getColor(slot.g2) }}
                    >
                      {slot.g2}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notice */}
        <div className="mt-6 flex items-start gap-3 bg-[#F5C518]/10 border border-[#F5C518]/30 rounded-xl p-4">
          <ChevronRight size={18} color="#F5C518" className="mt-0.5 flex-shrink-0" />
          <p className="font-['Montserrat'] text-sm text-[#475569]">
            <strong>Nota:</strong> El horario puede modificarse por razones organizativas o meteorológicas. El día de BTT se comunicará con antelación.
          </p>
        </div>
      </div>
    </section>
  );
}
